/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import API from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FilePdf, Handshake, CheckCircle, Receipt, ChatCircleText, PaperPlaneTilt } from "@phosphor-icons/react";

const formatGBP = (n) => new Intl.NumberFormat("en-GB", {
  style: "currency", currency: "GBP", maximumFractionDigits: 0,
}).format(n || 0);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return iso; }
};

const SEG_META = {
  quoted:      { title: "All Quoted",         hint: "Every live quote in the selected window.",                                icon: Receipt,      iconBg: "bg-slate-100 text-slate-700" },
  downloaded:  { title: "PDFs Downloaded",    hint: "Quotes where the PDF endpoint was hit at least once.",                  icon: FilePdf,      iconBg: "bg-amber-50 text-amber-700" },
  accepted:    { title: "Client Accepted",    hint: "Portal-accepted plus quotes a solicitor instructed on the client's behalf.", icon: Handshake, iconBg: "bg-blue-50 text-blue-700" },
  not_accepted: { title: "Not Yet Accepted",   hint: "Quoted but no acceptance signal yet — the chase-able cohort.",           icon: Handshake,    iconBg: "bg-rose-50 text-rose-700" },
  opened:      { title: "Case Opened",        hint: "Quotes that have produced a case.",                                      icon: CheckCircle,  iconBg: "bg-emerald-50 text-emerald-700" },
  portal:      { title: "Portal-Accepted",    hint: "Clients who clicked Accept on the public portal themselves.",            icon: Handshake,    iconBg: "bg-indigo-50 text-indigo-700" },
  direct:      { title: "Direct-Instructed",  hint: "Quotes a solicitor instructed without a portal click.",                  icon: ChatCircleText, iconBg: "bg-purple-50 text-purple-700" },
  not_downloaded:           { title: "Quoted but never downloaded", hint: "Quotes whose PDF link was never opened.",          icon: FilePdf,      iconBg: "bg-slate-100 text-slate-600" },
  downloaded_not_accepted:  { title: "Downloaded · not accepted",   hint: "Client saw the PDF but never agreed — a follow-up opportunity.", icon: FilePdf, iconBg: "bg-amber-50 text-amber-700" },
  accepted_not_opened:      { title: "Accepted · case not opened",  hint: "Client agreed but no case has been opened yet — workflow gap to chase.", icon: Handshake, iconBg: "bg-rose-50 text-rose-700" },
};

export default function QuoteFunnelSegmentDialog({ open, onOpenChange, segment, label, funnelDays, channel = "all", transactionType = "all", propertyType = "all" }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  // Per-row "chasing" state so the user gets immediate feedback when
  // they click and the button can be disabled while in-flight.
  const [chasing, setChasing] = useState({});  // { [quoteId]: boolean }

  useEffect(() => {
    if (!open || !segment) return;
    let cancelled = false;
    setLoading(true);
    setItems([]);
    setErrorMsg("");
    setChasing({});
    API.get("/quotes/funnel-segment", {
      params: { segment, funnel_days: funnelDays || 0, channel: channel || "all", transaction_type: transactionType || "all", property_type: propertyType || "all" },
    })
      .then(({ data }) => { if (!cancelled) setItems(data?.items || []); })
      .catch((err) => {
        if (cancelled) return;
        // Surface the failure prominently — silently empty dialogs
        // make it impossible for users to report bugs accurately.
        const status = err.response?.status;
        const detail = err.response?.data?.detail || err.message || "Unknown error";
        setErrorMsg(`API error${status ? ` ${status}` : ""}: ${detail}`);
        setItems([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, segment, funnelDays, channel, transactionType, propertyType]);

  // Segments where a chase email makes sense — i.e. the client could
  // still be nudged into accepting or instructing.
  const CHASEABLE_SEGMENTS = new Set([
    "not_accepted", "not_downloaded", "downloaded_not_accepted", "accepted_not_opened",
  ]);
  const isChaseable = CHASEABLE_SEGMENTS.has(segment);

  const handleChase = async (quote, e) => {
    e.stopPropagation();
    if (!quote.client_email) {
      toast.error("This quote has no client email — can't chase");
      return;
    }
    setChasing((p) => ({ ...p, [quote.id]: true }));
    try {
      const { data } = await API.post(`/quotes/${quote.id}/chase-email`);
      // Update the row locally so the user sees the chase counter tick
      // up without having to refetch the whole segment.
      setItems((prev) => prev.map((q) => q.id === quote.id ? {
        ...q,
        last_chase_at: new Date().toISOString(),
        chase_count: (q.chase_count || 0) + 1,
      } : q));
      if (data?.mode === "blocked") {
        toast.success("Chase email queued (blocked in dev — would send in production)");
      } else {
        toast.success(`Chase sent to ${quote.client_email}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send chase email");
    } finally {
      setChasing((p) => { const n = { ...p }; delete n[quote.id]; return n; });
    }
  };

  const meta = SEG_META[segment] || { title: label || segment, hint: "", icon: Receipt, iconBg: "bg-slate-100 text-slate-700" };
  const Icon = meta.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="funnel-segment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <div className={`w-8 h-8 ${meta.iconBg} rounded-lg flex items-center justify-center shrink-0`}>
              <Icon weight="fill" className="w-4 h-4" />
            </div>
            <span className="truncate">{meta.title}</span>
            <span className="text-xs font-normal text-slate-400 shrink-0">· {items.length} quote{items.length === 1 ? "" : "s"}</span>
          </DialogTitle>
          {meta.hint && (
            <DialogDescription className="text-xs">{meta.hint}</DialogDescription>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading && (
            <p className="text-xs text-slate-400 py-8 text-center" data-testid="funnel-segment-loading">Loading…</p>
          )}
          {!loading && errorMsg && (
            <div className="py-6 px-4 text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded-md" data-testid="funnel-segment-error">
              <p className="font-medium mb-1">Couldn't load quotes</p>
              <p className="font-mono text-[10px] break-all">{errorMsg}</p>
            </div>
          )}
          {!loading && !errorMsg && items.length === 0 && (
            <p className="text-xs text-slate-400 py-8 text-center" data-testid="funnel-segment-empty">No quotes in this segment.</p>
          )}
          {!loading && !errorMsg && items.length > 0 && (
            <ul className="divide-y divide-slate-100" data-testid="funnel-segment-list">
              {items.map((q) => (
                <li
                  key={q.id}
                  className="py-3 hover:bg-slate-50 cursor-pointer -mx-2 px-2 rounded transition-colors"
                  onClick={() => { navigate(`/quote/view/${q.id}`); onOpenChange(false); }}
                  data-testid={`funnel-row-${q.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{q.client_name || "—"}</p>
                      {q.client_email && (
                        <p className="text-[11px] text-slate-400 truncate">{q.client_email}</p>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-slate-900 shrink-0">{formatGBP(q.total)}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap text-[10px] text-slate-500">
                    <span className="capitalize">{q.transaction_type || "—"}</span>
                    {q.property_value && <span>· {formatGBP(q.property_value)}</span>}
                    <span>· {formatDate(q.created_at)}</span>
                    <span className={`px-1.5 py-0.5 rounded-full font-medium ${
                      q.source === "referrer" || q.source === "referrer_bespoke"
                        ? "bg-violet-50 text-violet-700"
                        : q.source === "solicitor"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-slate-100 text-slate-600"
                    }`}>
                      {q.source === "referrer" || q.source === "referrer_bespoke"
                        ? `via ${q.referrer_name}`
                        : q.source === "solicitor"
                          ? `by ${q.created_by_name || "Solicitor"}`
                          : "Public"}
                    </span>
                    {(q.pdf_downloads || 0) > 0 && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 font-medium">PDF</span>
                    )}
                    {q.client_instructed && (
                      <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-medium">Portal</span>
                    )}
                    {q.instructed && (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">Opened</span>
                    )}
                  </div>
                  {isChaseable && q.client_email && (
                    <div className="mt-2 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px] px-2 gap-1"
                        disabled={!!chasing[q.id]}
                        onClick={(e) => handleChase(q, e)}
                        data-testid={`chase-btn-${q.id}`}
                        title={q.last_chase_at
                          ? `Last chased ${formatDate(q.last_chase_at)} (${q.chase_count || 1}×)`
                          : "Send a templated nudge email to the client"}
                      >
                        <PaperPlaneTilt weight="fill" className="w-3 h-3" />
                        {chasing[q.id]
                          ? "Sending…"
                          : q.last_chase_at
                            ? `Chase ${(q.chase_count || 1)}×`
                            : "Send chase"}
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end pt-3 border-t border-slate-100 gap-2">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="funnel-segment-close">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
